import { z } from 'zod';

export const IntegrationProviderSchema = z.enum(['gmail_smtp', 'sendgrid', 'whatsapp', 'uwm', 'uwm_pricing', 'fcm_coda', 'pennymac_power', 'sunwest_sunsoft', 'amwest_pipeline', 'springeq_emma', 'tls_email', 'tls_portal', 'newrez_portal', 'cardinal_sheet']);
export type IntegrationProvider = z.infer<typeof IntegrationProviderSchema>;

// Gmail SMTP
export const GmailSmtpConfigSchema = z.object({
  fromEmail: z.string().email(),
  fromName: z.string().min(1).max(120),
  host: z.string().default('smtp.gmail.com'),
  port: z.coerce.number().int().positive().default(587),
  secure: z.boolean().default(false), // false = STARTTLS on 587, true = SSL on 465
});
export type GmailSmtpConfig = z.infer<typeof GmailSmtpConfigSchema>;

export const GmailSmtpSaveInputSchema = GmailSmtpConfigSchema.extend({
  appPassword: z.string().min(8).max(200),
});
export type GmailSmtpSaveInput = z.infer<typeof GmailSmtpSaveInputSchema>;

export const GmailSmtpTestInputSchema = z.object({
  to: z.string().email().optional(),
});
export type GmailSmtpTestInput = z.infer<typeof GmailSmtpTestInputSchema>;

// SendGrid (per-MLO, like Gmail SMTP): the v3 Mail Send API with an API key that has the Mail Send
// permission. The From address (or its domain) must be a verified Sender Identity in SendGrid.
export const SendGridConfigSchema = z.object({
  fromEmail: z.string().email(),
  fromName: z.string().min(1).max(120),
  /** Replies go here instead of the From address (optional). */
  replyTo: z.string().email().nullable().optional(),
});
export type SendGridConfig = z.infer<typeof SendGridConfigSchema>;

export const SendGridSaveInputSchema = SendGridConfigSchema.extend({
  /** Saved encrypted; blank on a later save keeps the stored key. */
  apiKey: z.string().trim().max(400).optional().or(z.literal('')),
});
export type SendGridSaveInput = z.infer<typeof SendGridSaveInputSchema>;

export const SendGridTestInputSchema = z.object({
  to: z.string().email().optional(),
});
export type SendGridTestInput = z.infer<typeof SendGridTestInputSchema>;

// WhatsApp
export const WhatsAppModeSchema = z.enum(['manual', 'cloud_api', 'twilio']);
export type WhatsAppMode = z.infer<typeof WhatsAppModeSchema>;

export const WhatsAppConfigSchema = z.object({
  mode: WhatsAppModeSchema,
  // cloud_api fields (Meta direct)
  phoneNumberId: z.string().nullable().optional(),
  wabaId: z.string().nullable().optional(),
  graphApiVersion: z.string().default('v20.0'),
  // twilio fields (BSP)
  twilioAccountSid: z.string().nullable().optional(),
  /**
   * Twilio WhatsApp-enabled sender in E.164 (with or without `whatsapp:`).
   * Either this OR `twilioMessagingServiceSid` must be set when mode='twilio'.
   * Messaging Service is preferred for production (failover, throughput
   * throttling, sender pool); a direct From number is fine for dev/sandbox.
   */
  twilioFromNumber: z.string().nullable().optional(),
  /**
   * Twilio Messaging Service SID (MG + 32 hex). When set, the sender posts
   * `MessagingServiceSid=...` instead of `From=whatsapp:+...` and Twilio
   * routes the message through whichever sender(s) the service has attached.
   * This is how the user's "ManaPlatformMessaging-WhatsApp" service is
   * configured — Twilio picks the registered +1312… number automatically.
   */
  twilioMessagingServiceSid: z.string().nullable().optional(),
  twilioDefaultContentSid: z.string().nullable().optional(),
  // Shared template metadata
  defaultTemplateName: z.string().nullable().optional(),
  defaultTemplateLanguage: z.string().default('en_US'),
});
export type WhatsAppConfig = z.infer<typeof WhatsAppConfigSchema>;

export const WhatsAppSaveInputSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('manual') }),
  z.object({
    mode: z.literal('cloud_api'),
    phoneNumberId: z.string().min(1),
    wabaId: z.string().min(1),
    accessToken: z.string().min(10),
    graphApiVersion: z.string().default('v20.0'),
    defaultTemplateName: z.string().optional(),
    defaultTemplateLanguage: z.string().default('en_US'),
  }),
  z.object({
    mode: z.literal('twilio'),
    // Twilio Account SIDs are exactly "AC" + 32 hex/alnum chars.
    accountSid: z
      .string()
      .regex(/^AC[a-zA-Z0-9]{32}$/, 'Twilio Account SID must look like AC... (34 chars).'),
    authToken: z.string().min(10),
    /**
     * E.164 phone (with or without leading `whatsapp:`). Sandbox: +14155238886.
     * Either this OR `messagingServiceSid` is required (enforced by the
     * `superRefine` below — discriminatedUnion variants can't be ZodEffects).
     */
    fromNumber: z.string().min(7).optional(),
    /**
     * Twilio Messaging Service SID (MG + 32 hex). When provided, Twilio routes
     * via the service's sender pool. Production-recommended.
     */
    messagingServiceSid: z
      .string()
      .regex(/^MG[a-zA-Z0-9]{32}$/, 'Messaging Service SID must look like MG... (34 chars).')
      .optional(),
    /** Optional Twilio Content SID (HX...) for out-of-window template sends. */
    defaultContentSid: z.string().optional(),
    defaultTemplateLanguage: z.string().default('en_US'),
  }),
]).superRefine((v, ctx) => {
  // Twilio mode requires either a From number or a Messaging Service SID —
  // hoisted out of the variant because z.discriminatedUnion rejects
  // ZodEffects-wrapped variants.
  if (v.mode === 'twilio' && !v.fromNumber && !v.messagingServiceSid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fromNumber'],
      message:
        'Provide either a From Number (sandbox or direct sender) or a Messaging Service SID.',
    });
  }
});
export type WhatsAppSaveInput = z.infer<typeof WhatsAppSaveInputSchema>;

export const WhatsAppTestInputSchema = z.object({
  to: z.string().min(7),
  message: z.string().max(1024).default('Mana Home Loans test message.'),
});
export type WhatsAppTestInput = z.infer<typeof WhatsAppTestInputSchema>;

// UWM
export const UwmConfigSchema = z.object({
  tokenUrl: z
    .string()
    .url()
    .default(
      'https://login.microsoftonline.com/13d3189d-22a4-4f96-bbc9-2af46c222581/oauth2/v2.0/token',
    ),
  scope: z.string().default('api://5260c3e4-e629-4b1c-b26d-3bb08407fc75/.default'),
  apiEndpoint: z.string().url().default('https://api.uwm.com/brokers/insights/'),
  mode: z.enum(['mock', 'real']).default('mock'),
});
export type UwmConfig = z.infer<typeof UwmConfigSchema>;

export const UwmSaveInputSchema = UwmConfigSchema.extend({
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
});
export type UwmSaveInput = z.infer<typeof UwmSaveInputSchema>;

// UWM Pricing (Instant Price Quote API) — a SEPARATE UWM API with its own
// credentials and ADFS auth (vs Insights' Azure AD). Each environment has its
// own client/secret: staging creds on the dev app, prod creds on the prod box.
export const UwmPricingEnvSchema = z.enum(['staging', 'production']);
export type UwmPricingEnv = z.infer<typeof UwmPricingEnvSchema>;

/** Per-environment scope + API host (token URL is the same ADFS endpoint for both). */
export const UWM_PRICING_ENVS: Record<UwmPricingEnv, { scope: string; baseUrl: string }> = {
  staging: { scope: 'https://api.uwm.com/loanorigination-stg', baseUrl: 'https://stg.api.uwm.com' },
  production: { scope: 'https://api.uwm.com/loanorigination', baseUrl: 'https://api.uwm.com' },
};

export const UwmPricingConfigSchema = z.object({
  environment: UwmPricingEnvSchema.default('staging'),
  tokenUrl: z.string().url().default('https://sso.uwm.com/adfs/oauth2/token'),
  /** Optional UWM broker alias (e.g. "MI0000") attributed on every quote. */
  brokerAlias: z.string().nullable().optional(),
  /**
   * UWM username (EASE email). When set, the token call uses the PASSWORD grant
   * (a UWM "user" credential) instead of client-credentials. Non-secret, so it
   * is echoed back to the UI for pre-population; the password lives in the
   * encrypted secret blob.
   */
  username: z.string().nullable().optional(),
});
export type UwmPricingConfig = z.infer<typeof UwmPricingConfigSchema>;

export const UwmPricingSaveInputSchema = UwmPricingConfigSchema.extend({
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
  /** Required only when `username` is set (password grant). */
  password: z.string().optional(),
}).superRefine((v, ctx) => {
  if (v.username && v.username.trim() && !v.password) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['password'],
      message: 'Password is required when a username is set (UWM user credential).',
    });
  }
});
export type UwmPricingSaveInput = z.infer<typeof UwmPricingSaveInputSchema>;

/**
 * First Colony (FCM TPO) broker portal "CODA" — rate-sheet download login. Org-level.
 * The portal login (username) is non-secret config; the password lives in the encrypted
 * secret blob. `tierKeys` = which portal rate-sheet groups to pull ("broker" = the
 * wholesale/broker sheets; "ndl1..3" are the non-delegated correspondent tiers).
 */
export const FcmCodaConfigSchema = z.object({
  username: z.string().min(1),
  tierKeys: z.array(z.string().min(1)).default(['broker']),
});
export type FcmCodaConfig = z.infer<typeof FcmCodaConfigSchema>;

export const FcmCodaSaveInputSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
  tierKeys: z.array(z.string().min(1)).optional(),
});
export type FcmCodaSaveInput = z.infer<typeof FcmCodaSaveInputSchema>;

/**
 * PennyMac TPO "POWER+" portal login (Encompass TPO Connect with PennyMac's SSO) — used
 * by the hourly rate-sheet pull, which drives a headless browser through the portal's
 * sign-in and downloads the newest Best Effort broker sheet. Org-level; the password
 * lives in the encrypted secret blob.
 */
export const PennymacPowerConfigSchema = z.object({
  username: z.string().min(1),
  /** Whether an authenticator (TOTP) key is stored with the password — the SSO asks new browsers for a 6-digit code. */
  totpConfigured: z.boolean().default(false),
});
export type PennymacPowerConfig = z.infer<typeof PennymacPowerConfigSchema>;

export const PennymacPowerSaveInputSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
  /** Base32 authenticator key from PennyMac's MFA enrollment ("enter the code manually"). Optional; spaces ignored. */
  totpSecret: z
    .string()
    .trim()
    .transform((s) => s.replace(/\s+/g, '').toUpperCase())
    .refine((s) => s === '' || /^[A-Z2-7=]{16,128}$/.test(s), 'The authenticator key should be the base32 text shown at enrollment (letters A–Z and digits 2–7).')
    .optional(),
});
export type PennymacPowerSaveInput = z.infer<typeof PennymacPowerSaveInputSchema>;

/**
 * Sun West "SunSoft" login — the credential behind "Rate Sheets" on swmc.com/tpo. The hourly
 * pull signs in and downloads the wholesale rate sheet (Excel) through the same service the
 * website uses. Org-level; the password lives in the encrypted secret blob. A company code is
 * only asked of Sun West staff logins (a broker's own login already belongs to its company).
 */
export const SunwestSunsoftConfigSchema = z.object({
  username: z.string().min(1),
  companyCode: z.string().default(''),
  /** Set when SunSoft refused this login; the hourly pull pauses (so retries can't lock the account) until it is re-saved or tested. */
  signInRefusedAt: z.string().nullable().optional(),
});
export type SunwestSunsoftConfig = z.infer<typeof SunwestSunsoftConfigSchema>;

export const SunwestSunsoftSaveInputSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
  companyCode: z.string().trim().max(40).optional(),
});
export type SunwestSunsoftSaveInput = z.infer<typeof SunwestSunsoftSaveInputSchema>;

/**
 * AmWest Funding broker portal (pipeline.amwestwholesale.com) login — the hourly pull signs in
 * (plain form, no one-time code) and downloads the newest wholesale rate-sheet PDF from Rate
 * Sheets. Org-level; the password lives in the encrypted secret blob.
 */
export const AmwestPipelineConfigSchema = z.object({
  username: z.string().min(1),
  /** Set when the portal refused this login twice in a row; the hourly pull pauses until it is re-saved or tested. */
  signInRefusedAt: z.string().nullable().optional(),
});
export type AmwestPipelineConfig = z.infer<typeof AmwestPipelineConfigSchema>;

export const AmwestPipelineSaveInputSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
});
export type AmwestPipelineSaveInput = z.infer<typeof AmwestPipelineSaveInputSchema>;

/**
 * Spring EQ EMMA (emma.springeq.com) login — the hourly pull signs in and downloads the newest
 * wholesale home-equity rate-sheet PDF from RateSheets. Org-level; the password lives in the
 * encrypted secret blob.
 */
export const SpringeqEmmaConfigSchema = z.object({
  email: z.string().min(1),
  signInRefusedAt: z.string().nullable().optional(),
});
export type SpringeqEmmaConfig = z.infer<typeof SpringeqEmmaConfigSchema>;

export const SpringeqEmmaSaveInputSchema = z.object({
  email: z.string().trim().min(3),
  password: z.string().min(1),
});
export type SpringeqEmmaSaveInput = z.infer<typeof SpringeqEmmaSaveInputSchema>;

/**
 * The Loan Store rate-sheet mailbox — TLS emails its workbook ("TLS <date> - Price Update" from
 * tls.rates@theloanstore.com) every time it reprices; Mana reads the mailbox over IMAP with a
 * Google app password (read-only: nothing is marked, moved or deleted). Org-level; the app
 * password lives in the encrypted secret blob.
 */
export const TlsEmailConfigSchema = z.object({
  email: z.string().min(1),
  /** IMAP host (Gmail by default). */
  host: z.string().optional(),
  signInRefusedAt: z.string().nullable().optional(),
});
export type TlsEmailConfig = z.infer<typeof TlsEmailConfigSchema>;

export const TlsEmailSaveInputSchema = z.object({
  email: z.string().trim().email(),
  /** Google app password (16 letters; spaces are ignored). */
  appPassword: z.string().transform((s) => s.replace(/\s+/g, '')).pipe(z.string().min(8)),
});
export type TlsEmailSaveInput = z.infer<typeof TlsEmailSaveInputSchema>;

/**
 * Cardinal Financial's rate sheet — which of Cardinal's pricing TIERS the daily import stores.
 * Cardinal publishes the same wholesale sheet as ten tier files (CF_WL_810021 … CF_WL_837021,
 * ladders ~0.35 pt apart); its "Today's Rates" email links one of them, Mana prices on the tier
 * that matched Octane's quotes. `tier` null = import exactly the file the email links. Org-level,
 * no secret (the email is read from the rate-sheet mailbox saved under tls_email).
 */
export const CardinalSheetConfigSchema = z.object({
  /** "828021" — the CF_WL_<tier>.xls file to store; null follows the email's link. */
  tier: z.string().regex(/^\d{4,8}$/).nullable(),
});
export type CardinalSheetConfig = z.infer<typeof CardinalSheetConfigSchema>;

export const CardinalSheetSaveInputSchema = z.object({
  /** "828021", "CF_WL_828021.xls" or blank (= follow the email's link). */
  tier: z
    .string()
    .trim()
    .transform((s) => s.replace(/^CF_WL_/i, '').replace(/\.xlsx?$/i, '').trim())
    .pipe(z.union([z.literal(''), z.string().regex(/^\d{4,8}$/, 'the tier is the number in the file name, e.g. 828021 for CF_WL_828021.xls')])),
});
export type CardinalSheetSaveInput = z.infer<typeof CardinalSheetSaveInputSchema>;

/**
 * The Loan Store portal login — the ICE Encompass / TPO Connect sign-in
 * (theloanstore.encompasstpoconnect.com), used by the nightly audit to price scenarios on TLS's
 * own portal (Polly) and confirm Mana's sheet prices match. Org-level; the password lives in the
 * encrypted secret blob.
 */
export const TlsPortalConfigSchema = z.object({
  username: z.string().min(1),
  signInRefusedAt: z.string().nullable().optional(),
});
export type TlsPortalConfig = z.infer<typeof TlsPortalConfigSchema>;

export const TlsPortalSaveInputSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
});
export type TlsPortalSaveInput = z.infer<typeof TlsPortalSaveInputSchema>;

/**
 * Newrez Wholesale broker portal (pricing.newrez.com, "Loan Advisor", Okta sign-in at
 * auth.newrez.com — user name and password, no one-time code) — the hourly pull signs in and
 * downloads the newest Wholesale rate-sheet workbook from View Ratesheets. Org-level; the password
 * lives in the encrypted secret blob.
 */
export const NewrezPortalConfigSchema = z.object({
  username: z.string().min(1),
  /** Set when Okta refused this login twice in a row; the hourly pull pauses until it is re-saved or tested. */
  signInRefusedAt: z.string().nullable().optional(),
});
export type NewrezPortalConfig = z.infer<typeof NewrezPortalConfigSchema>;

export const NewrezPortalSaveInputSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
});
export type NewrezPortalSaveInput = z.infer<typeof NewrezPortalSaveInputSchema>;

export const IntegrationStatusSchema = z.object({
  provider: IntegrationProviderSchema,
  configured: z.boolean(),
  lastTestedAt: z.string().datetime().nullable().optional(),
  lastTestStatus: z.enum(['success', 'failure']).nullable().optional(),
  lastTestError: z.string().nullable().optional(),
});
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;
